import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';
import { LuCameraOff, LuLoaderCircle, LuLogIn } from 'react-icons/lu';
import Button from '../../components/ui/Button';
import { useTranslation } from '../../i18n/LanguageContext';

// The scan counts down from 5, one step every 0.8 s, then takes the photo
const COUNTDOWN_FROM = 5;
const TICK_MS = 800;
// After a camera error the login continues on its own after this delay
const ERROR_CONTINUE_MS = 1500;

// Decorative landmark dots over the face guide (percent of the frame)
const FACE_POINTS = [
  [50, 30], [41, 41], [59, 41], [50, 50], [43, 60], [57, 60], [50, 64], [35, 50], [65, 50], [50, 74],
];

const CORNERS = [
  'tw-left-3 tw-top-3 tw-border-l-2 tw-border-t-2 tw-rounded-tl-xl',
  'tw-right-3 tw-top-3 tw-border-r-2 tw-border-t-2 tw-rounded-tr-xl',
  'tw-bottom-3 tw-left-3 tw-border-b-2 tw-border-l-2 tw-rounded-bl-xl',
  'tw-bottom-3 tw-right-3 tw-border-b-2 tw-border-r-2 tw-rounded-br-xl',
];

const cameraErrorKey = (error) => {
  if (error?.name === 'NotAllowedError') return 'denied';
  if (error?.name === 'NotFoundError') return 'notFound';
  return 'failed';
};

const StatusRow = ({ label, value, tone }) => (
  <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-text-xs">
    <span className="tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">{label}</span>
    <span className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-font-semibold tw-tabular-nums ${tone === 'off' ? 'tw-text-rose-600 dark:tw-text-rose-300' : 'tw-text-teal-700 dark:tw-text-teal-300'}`}>
      <span aria-hidden="true" className={`tw-h-1.5 tw-w-1.5 tw-rounded-full ${tone === 'off' ? 'tw-bg-rose-500' : 'tw-animate-pulse tw-bg-teal-500'}`} />
      {value}
    </span>
  </div>
);

StatusRow.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.string.isRequired, tone: PropTypes.string };

/**
 * The face scan shown while signing in: the camera feed in a scanner frame
 * and a short countdown, after which one photo is taken for the login record.
 * Without a camera (no support, denied, missing) the login continues on its
 * own; "Şimdi Giriş Yap" skips the wait. `onDone(photo)` is called once, with
 * a JPEG data URL or null.
 */
const FaceScan = ({ onDone }) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const doneRef = useRef(false);
  // Latest callback, so a parent re-render never restarts the camera or the countdown
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const [status, setStatus] = useState('starting'); // starting | live | error | playback
  const [errorKey, setErrorKey] = useState(null);
  const [countdown, setCountdown] = useState(COUNTDOWN_FROM);
  const [flash, setFlash] = useState(false);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch (error) {
        // already stopped
      }
    });
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const capture = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return null;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;
      canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
      setFlash(true);
      return canvas.toDataURL('image/jpeg', 0.85);
    } catch (error) {
      console.error('Error taking snapshot:', error);
      return null;
    }
  }, []);

  const finish = useCallback((takePhoto) => {
    if (doneRef.current) return;
    doneRef.current = true;
    const photo = takePhoto ? capture() : null;
    stopCamera();
    // A moment for the capture flash before the login continues
    setTimeout(() => onDoneRef.current(photo), photo ? 350 : 0);
  }, [capture, stopCamera]);

  // Open the camera
  useEffect(() => {
    let cancelled = false;
    let errorTimer;
    const fail = (key) => {
      setErrorKey(key);
      setStatus('error');
      errorTimer = setTimeout(() => finish(false), ERROR_CONTINUE_MS);
    };

    if (!navigator.mediaDevices?.getUserMedia) {
      fail('unsupported');
      return () => clearTimeout(errorTimer);
    }

    navigator.mediaDevices
      .getUserMedia({ video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' }, audio: false })
      .then((stream) => {
        if (cancelled || doneRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        video.play()
          .then(() => !cancelled && setStatus('live'))
          .catch((error) => {
            console.error('Error playing video:', error);
            if (!cancelled) setStatus('playback');
          });
      })
      .catch((error) => {
        console.error('Error accessing camera:', error);
        if (!cancelled) fail(cameraErrorKey(error));
      });

    return () => {
      cancelled = true;
      clearTimeout(errorTimer);
      stopCamera();
    };
  }, [finish, stopCamera]);

  // Count down while the scanner is up (as before, from the moment it opens)
  useEffect(() => {
    if (status === 'error' || status === 'playback') return undefined;
    if (countdown <= 0) {
      finish(true);
      return undefined;
    }
    const timer = setTimeout(() => setCountdown((value) => value - 1), TICK_MS);
    return () => clearTimeout(timer);
  }, [countdown, status, finish]);

  const progress = (COUNTDOWN_FROM - countdown) / COUNTDOWN_FROM;
  const cameraValue = { starting: t('login.scan.cameraStarting'), live: t('login.scan.cameraOn') }[status] || t('login.scan.cameraOff');
  const unavailable = status === 'error' || status === 'playback';

  return (
    <div className="tw-space-y-5">
      <div className="tw-text-center">
        <h2 className="tw-text-xl tw-font-semibold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">{t('login.scan.title')}</h2>
        <p className="tw-mt-1 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{t('login.scan.subtitle')}</p>
      </div>

      <div className="tw-relative tw-aspect-[4/3] tw-overflow-hidden tw-rounded-3xl tw-bg-slate-950 tw-ring-1 tw-ring-white/10 tw-shadow-[0_20px_50px_-24px_rgba(13,148,136,0.6)]">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`tw-h-full tw-w-full tw-object-cover tw-transition-opacity tw-duration-500 [transform:scaleX(-1)] ${status === 'live' ? 'tw-opacity-100' : 'tw-opacity-0'}`}
        />

        {/* Scanner overlay */}
        <div aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-inset-0">
          <div className="tw-absolute tw-inset-0 tw-opacity-30 [background-image:linear-gradient(rgba(45,212,191,0.18)_1px,transparent_1px),linear-gradient(90deg,rgba(45,212,191,0.18)_1px,transparent_1px)] [background-size:24px_24px]" />
          <div className="tw-absolute tw-inset-0 tw-bg-[radial-gradient(ellipse_at_center,transparent_45%,rgba(2,6,23,0.65)_100%)]" />
          {CORNERS.map((corner) => (
            <span key={corner} className={`tw-absolute tw-h-8 tw-w-8 tw-border-teal-300/90 ${corner}`} />
          ))}
          <div className="tw-absolute tw-left-1/2 tw-top-1/2 tw-h-[72%] tw-w-[46%] -tw-translate-x-1/2 -tw-translate-y-1/2 tw-rounded-[50%] tw-border-2 tw-border-dashed tw-border-teal-300/60" />
          <motion.div
            className="tw-absolute tw-left-1/2 tw-top-1/2 tw-h-[72%] tw-w-[46%] tw-rounded-[50%] tw-border tw-border-teal-300/40"
            style={{ x: '-50%', y: '-50%' }}
            animate={{ scale: [1, 1.08, 1], opacity: [0.6, 0, 0.6] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
          />
          {status === 'live' && FACE_POINTS.map(([left, top], index) => (
            <motion.span
              key={`${left}-${top}`}
              className="tw-absolute tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-teal-300 tw-shadow-[0_0_8px_rgba(45,212,191,0.9)]"
              style={{ left: `${left}%`, top: `${top}%`, x: '-50%', y: '-50%' }}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: [0, 1, 0.5, 1], scale: 1 }}
              transition={{ delay: 0.15 * index, duration: 1.6, repeat: Infinity, repeatType: 'reverse' }}
            />
          ))}
          {!unavailable && (
            <motion.div
              className="tw-absolute tw-inset-x-6 tw-h-0.5 tw-rounded-full tw-bg-gradient-to-r tw-from-transparent tw-via-teal-300 tw-to-transparent tw-shadow-[0_0_16px_4px_rgba(45,212,191,0.55)]"
              animate={{ top: ['12%', '88%', '12%'] }}
              transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
            />
          )}
        </div>

        {status === 'starting' && (
          <div className="tw-absolute tw-inset-0 tw-grid tw-place-items-center">
            <p className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-teal-100">
              <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
              {t('login.scan.starting')}
            </p>
          </div>
        )}
        {unavailable && (
          <div role="alert" className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-px-6 tw-text-center">
            <span className="tw-grid tw-h-12 tw-w-12 tw-place-items-center tw-rounded-2xl tw-bg-white/10 tw-text-rose-300">
              <LuCameraOff aria-hidden="true" className="tw-h-6 tw-w-6" />
            </span>
            <p className="tw-text-sm tw-font-medium tw-text-slate-100">
              {status === 'playback' ? t('login.scan.errors.playback') : t(`login.scan.errors.${errorKey}`)}
            </p>
            {status === 'playback' && (
              <Button size="sm" onClick={() => finish(false)} className="tw-pointer-events-auto">{t('login.scan.continue')}</Button>
            )}
          </div>
        )}

        {/* Countdown ring */}
        {!unavailable && (
          <div className="tw-absolute tw-bottom-3 tw-right-3 tw-grid tw-h-12 tw-w-12 tw-place-items-center tw-rounded-full tw-bg-slate-950/60">
            <svg viewBox="0 0 36 36" aria-hidden="true" className="tw-absolute tw-inset-0 tw-h-full tw-w-full -tw-rotate-90">
              <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
              <circle
                cx="18"
                cy="18"
                r="15.5"
                fill="none"
                stroke="#2dd4bf"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={97.4}
                strokeDashoffset={97.4 * (1 - progress)}
                className="tw-transition-[stroke-dashoffset] tw-duration-700 tw-ease-linear"
              />
            </svg>
            <span className="tw-relative tw-text-sm tw-font-bold tw-text-white tw-tabular-nums">{countdown}</span>
          </div>
        )}

        <AnimatePresence>
          {flash && (
            <motion.div
              key="flash"
              aria-hidden="true"
              className="tw-absolute tw-inset-0 tw-bg-white"
              initial={{ opacity: 0.9 }}
              animate={{ opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
              onAnimationComplete={() => setFlash(false)}
            />
          )}
        </AnimatePresence>
      </div>

      <div className="tw-space-y-2 tw-rounded-2xl tw-bg-slate-900/[0.03] tw-px-4 tw-py-3 dark:tw-bg-white/[0.04]" aria-live="polite">
        <StatusRow label={t('login.scan.camera')} value={cameraValue} tone={unavailable ? 'off' : 'on'} />
        <StatusRow
          label={t('login.scan.photo')}
          value={countdown <= 0 ? t('login.scan.photoTaken') : t('login.scan.photoIn', { count: countdown })}
          tone={unavailable ? 'off' : 'on'}
        />
        <div className="tw-pt-1">
          <div className="tw-flex tw-items-center tw-justify-between tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500">
            <span className="tw-inline">{t('login.scan.processing')}</span>
            <span className="tw-tabular-nums">{Math.round(progress * 100)}%</span>
          </div>
          <div className="tw-mt-1.5 tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10">
            <div
              className="tw-h-full tw-rounded-full tw-bg-gradient-to-r tw-from-teal-400 tw-to-cyan-500 tw-transition-[width] tw-duration-700 tw-ease-linear"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
        </div>
      </div>

      <Button size="lg" onClick={() => finish(status === 'live')} className="tw-w-full">
        <LuLogIn aria-hidden="true" className="tw-h-4 tw-w-4" />
        {t('login.scan.skip')}
      </Button>
    </div>
  );
};

FaceScan.propTypes = {
  onDone: PropTypes.func.isRequired,
};

export default FaceScan;
